import { memo, useCallback, useMemo, type FC } from "react";
import { Typography } from "@mui/material";
import type { CompanyFilters } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";
import { ProductAttributesInput } from "@/components/filters/ProductAttributes/ProductAttributesInput";

type BuyerIntentAttributesSlice = { attributeIds: number[] };
const defaultSlice: BuyerIntentAttributesSlice = { attributeIds: [] };

const BuyerIntentAttributesFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("buyerIntentAttributes");
  const { setSlice } = useCompanyFilterActions();

  const selectedItems = useMemo(
    () => slice.attributeIds.map(String),
    [slice.attributeIds]
  );

  const handleAttributesSelect = useCallback(
    (ids: string[]) => {
      setSlice("buyerIntentAttributes", { attributeIds: ids.map(Number) });
    },
    [setSlice]
  );

  return (
    <>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Include companies researching any of the following attributes
      </Typography>
      <ProductAttributesInput
        selectedItems={selectedItems}
        onProductAttributesSelect={handleAttributesSelect}
        id="buyer-intent-attributes-input"
      />
    </>
  );
});

BuyerIntentAttributesFilter.displayName = "BuyerIntentAttributesFilter";

export const buyerIntentAttributesModule: FilterModule<BuyerIntentAttributesSlice> =
  {
    key: "buyerIntentAttributes",
    defaultSlice,
    Component: BuyerIntentAttributesFilter,
    toCompanyFilters: (slice) =>
      slice.attributeIds.length > 0
        ? { buyerIntentAttributes: { ids: slice.attributeIds } }
        : {},
    fromCompanyFilters: (filters: CompanyFilters) => ({
      attributeIds:
        filters.buyerIntentAttributes?.ids?.filter(
          (id): id is number => id != null
        ) ?? [],
    }),
    getChips: (slice, setSlice) =>
      slice.attributeIds.length === 0
        ? []
        : [
            {
              id: "bi-attributes",
              label: `BI Attributes: ${slice.attributeIds.length} selected`,
              filterKey: "buyerIntentAttributes" as const,
              onRemove: () => setSlice({ attributeIds: [] }),
            },
          ],
    getCount: (slice) => slice.attributeIds.length,
  };
